import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import GearButton from "../ui/gearbutton";
import { Contact, User } from "../types/User";
import { updateProfile } from "../backend/api/auth";
import { UpdateProfileRequest } from "../backend/types/api-types";

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: User;
  onSuccess?: (updatedUser: User) => void;
}

const contactTypeOptions: Contact["type"][] = [
  "email",
  "instagram",
  "x",
  "linkedin",
  "facebook",
  "website",
  "telegram",
];

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = useState(initialData?.firstName ?? "");
  const [lastName, setLastName] = useState(initialData?.lastName ?? "");
  const [contacts, setContacts] = useState<Contact[]>(
    initialData?.contacts ?? []
  );
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: UpdateProfileRequest) =>
      updateProfile(payload, i18n.language),
    onSuccess: (updatedUser) => {
      setFormError(null);
      onSuccess?.(updatedUser);
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        setFormError(error.message);
        return;
      }
      setFormError(
        t(
          "pages.profile.modalGenericError",
          "Qualcosa è andato storto. Riprova più tardi."
        )
      );
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    setFirstName(initialData?.firstName ?? "");
    setLastName(initialData?.lastName ?? "");
    setContacts(initialData?.contacts ?? []);
    setFormError(null);
  }, [initialData, isOpen]);

  const sanitizedInitialContacts = useMemo(
    () =>
      (initialData?.contacts ?? []).map((contact) => ({
        type: contact.type,
        value: contact.value.trim(),
      })),
    [initialData?.contacts]
  );

  const sanitizedCurrentContacts = useMemo(
    () =>
      contacts.map((contact) => ({
        type: contact.type,
        value: contact.value.trim(),
      })),
    [contacts]
  );

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const trimmedInitialFirstName = (initialData?.firstName ?? "").trim();
  const trimmedInitialLastName = (initialData?.lastName ?? "").trim();

  const isDirty = useMemo(() => {
    if (!initialData) return true;
    const hasNameChange =
      trimmedFirstName !== trimmedInitialFirstName ||
      trimmedLastName !== trimmedInitialLastName;

    if (sanitizedInitialContacts.length !== sanitizedCurrentContacts.length) {
      return true;
    }

    const contactsChanged = sanitizedCurrentContacts.some((contact, index) => {
      const original = sanitizedInitialContacts[index];
      return (
        !original ||
        original.type !== contact.type ||
        original.value !== contact.value
      );
    });

    return hasNameChange || contactsChanged;
  }, [
    initialData,
    sanitizedCurrentContacts,
    sanitizedInitialContacts,
    trimmedFirstName,
    trimmedInitialFirstName,
    trimmedInitialLastName,
    trimmedLastName,
  ]);

  const handleContactChange = (
    index: number,
    field: keyof Contact,
    value: string
  ) => {
    setContacts((prev) =>
      prev.map((contact, i) =>
        i === index
          ? {
              ...contact,
              [field]: value,
            }
          : contact
      )
    );
  };

  const handleRemoveContact = (index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddContact = () => {
    setContacts((prev) => [...prev, { type: "email", value: "" }]);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const payload: UpdateProfileRequest = {};

    if (trimmedFirstName !== trimmedInitialFirstName) {
      payload.firstName = trimmedFirstName;
    }

    if (trimmedLastName !== trimmedInitialLastName) {
      payload.lastName = trimmedLastName;
    }

    const sanitizedContactsWithoutEmpty = sanitizedCurrentContacts.filter(
      (contact) => contact.value.length > 0
    );

    const contactsChanged =
      sanitizedContactsWithoutEmpty.length !==
        sanitizedInitialContacts.length ||
      sanitizedContactsWithoutEmpty.some((contact, index) => {
        const original = sanitizedInitialContacts[index];
        return (
          !original ||
          original.type !== contact.type ||
          original.value !== contact.value
        );
      });

    if (contactsChanged) {
      payload.contacts = sanitizedContactsWithoutEmpty;
    }

    mutation.mutate(payload);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#1F1235] border-modal shadow-[0_30px_60px_rgba(3,0,28,0.55)]">
        <div className="flex justify-between items-start px-6 pt-6">
          <div>
            <h2 className="text-2xl font-semibold text-secondary mt-2">
              {t("pages.profile.modalTitle", "Aggiorna i tuoi dettagli")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white"
            type="button"
          >
            ✕
          </button>
        </div>

        <form className="px-6 pb-6 pt-4" onSubmit={handleSubmit}>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-2">
              <span className="text-sm text-white/80">
                {t("forms.firstName", "Nome")}
              </span>
              <input
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder={t("forms.firstName", "Nome")}
                className="rounded-xl bg-[#2C1A4A] border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary/60"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-sm text-white/80">
                {t("forms.lastName", "Cognome")}
              </span>
              <input
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder={t("forms.lastName", "Cognome")}
                className="rounded-xl bg-[#2C1A4A] border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary/60"
              />
            </label>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-white font-medium">
                  {t("forms.contactInformation", "Informazioni di Contatto")}
                </p>
                <p className="text-white/60 text-sm">
                  {t(
                    "forms.contactHint",
                    "Aggiungi i tuoi metodi di contatto preferiti."
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddContact}
                className="text-sm text-primary hover:text-primary/80"
              >
                {t("forms.addContactMethod", "Aggiungi Metodo di Contatto")}
              </button>
            </div>

            {contacts.length === 0 && (
              <div className="rounded-xl border border-dashed border-white/15 bg-white/5 px-4 py-6 text-center text-white/60 text-sm">
                {t(
                  "pages.profile.noContacts",
                  "Nessun contatto aggiunto. Aggiungine uno per aiutare altri utenti a raggiungerti."
                )}
              </div>
            )}

            <div className="flex flex-col gap-3">
              {contacts.map((contact, index) => (
                <div
                  key={`${contact.type}-${index}`}
                  className="flex flex-col md:flex-row gap-3 rounded-2xl bg-[#3A1A60] border border-white/10 p-4"
                >
                  <div className="md:w-1/3">
                    <label className="text-xs uppercase tracking-widest text-white/50">
                      {t("forms.type", "Tipo")}
                    </label>
                    <select
                      value={contact.type}
                      onChange={(event) =>
                        handleContactChange(index, "type", event.target.value)
                      }
                      className="mt-2 w-full rounded-xl bg-[#35205B] border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-primary/60"
                    >
                      {contactTypeOptions.map((option) => (
                        <option value={option} key={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1">
                    <label className="text-xs uppercase tracking-widest text-white/50">
                      {t(
                        "forms.contactInformation",
                        "Informazioni di Contatto"
                      )}
                    </label>
                    <input
                      value={contact.value}
                      onChange={(event) =>
                        handleContactChange(index, "value", event.target.value)
                      }
                      placeholder={t("forms.enterContactPlaceholder", {
                        defaultValue: "Inserisci il tuo contatto {{type}}",
                        type: contact.type,
                      })}
                      className="mt-2 w-full rounded-xl bg-[#35205B] border border-white/10 px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/60"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveContact(index)}
                    className="text-sm self-end mt-6 text-red-300 hover:text-red-200 md:self-center"
                  >
                    {t("forms.remove", "Rimuovi")}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {formError && (
            <div className="mt-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {formError}
            </div>
          )}

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 rounded-xl border border-white/15 text-white/90 hover:text-white hover:border-white/40 transition"
            >
              {t("forms.cancel", "Annulla")}
            </button>
            <GearButton
              type="submit"
              disabled={!isDirty || mutation.isPending}
              className="rounded-xl"
            >
              {mutation.isPending
                ? t("forms.updating", "Aggiornamento in corso...")
                : t("forms.saveChanges", "Salva Modifiche")}
            </GearButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
